/**
 * toastMapper (WEB-435)
 *
 * Pure projection from a live `userNotifications` WS frame to the toast input
 * consumed by the canonical `<ToastContainer>` (via `showNotification` from
 * `@loci/store`).
 *
 * The toast trigger is severity, not kind: a frame raises a toast iff
 * `severity === 'high'`. `kind` only selects the toast's visual `type`
 * (success/info/warning/error) so the surface stays consistent with the rest
 * of the notification system. The copy comes straight from the payload's
 * server-authored `title`/`body` — these are the source of truth for live
 * notifications, so no static `notificationMessages` constant applies.
 */

import type { NotificationInput } from '@loci/store';
import type { UserNotification, UserNotificationKind } from '@/hooks/useUserNotifications';

/**
 * Map a notification `kind` to the toast visual `type`. Kept exhaustive so a
 * new kind forces a deliberate choice here rather than silently defaulting.
 */
function kindToToastType(kind: UserNotificationKind): NotificationInput['type'] {
  switch (kind) {
    case 'TICKET_DROP':
      return 'success';
    case 'PRICE_CHANGE':
      return 'warning';
    case 'EVENT_REMINDER':
    case 'SAVED_EVENT_UPDATE':
    case 'SYSTEM':
    default:
      return 'info';
  }
}

/**
 * Project a high-severity user notification onto a toast input, or return
 * `null` when the frame must NOT toast (severity is not `high`, or absent —
 * an absent severity is treated as `normal` for forward-compatibility with
 * older servers).
 *
 * High-severity toasts are mapped at `priority: 'high'` and remain dismissible
 * with the standard auto-dismiss (the slice default of ~5s applies when
 * `duration` is omitted).
 */
export function mapHighSeverityNotificationToToast(
  notification: UserNotification,
): NotificationInput | null {
  if (notification.severity !== 'high') {
    return null;
  }

  return {
    type: kindToToastType(notification.kind),
    title: notification.title,
    message: notification.body ?? undefined,
    priority: 'high',
    dismissible: true,
  };
}
